import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { KycStatus, useContextDispatch, useContextState, } from '../../context/context';
import {
  GreenIdContainer,
  GreenIdUi,
} from '../../../shared/formElements/greenId/GreenId.styles';
import axios from 'axios';
import { Markdown } from '../../../shared/markdown';
import {
  LoadingSpinner,
  SectionContainer,
} from '../../../shared/Layout.styles';

export const GreenIdWebView = () => {
  const { loading, kyc, cardholderAuth} = useContextState();
  const history = useHistory();
  const push = history.push;
  const dispatch = useContextDispatch();
  const [serverErrors, setServerErrors] = useState('');
  // green id fires sessionCompleteCallback immediately if shown a token that has already been completed,
  // so make sure we only set it up once per token and only handle completion once
  const initialisedTokenRef = useRef<string | null>(null);
  const sessionCompletedRef = useRef(false);
  // read the latest values inside green id's callbacks without re-running the setup effect when they change
  const latestRef = useRef({ cardId: kyc.cardId, cardholderAuth });
  latestRef.current = { cardId: kyc.cardId, cardholderAuth };

  const onError = () => {
    setServerErrors('There was an issue with your submission. Please try again. If the problem persists, please contact support.');
  };

  const onSubmitValidation = () => {
    return true;
  };

  useEffect(() => {
    if (!kyc.verificationToken) {
      history.replace('/kyc-verification');
      return;
    }
    if (initialisedTokenRef.current === kyc.verificationToken) {
      return;
    }
    initialisedTokenRef.current = kyc.verificationToken;
    sessionCompletedRef.current = false;

    const onSessionCancel = () => {
      history.replace('/kyc-verification');
      return;
    }
    // check if the card is compliant at green id or not
    const handleKycConfirm = async (): Promise<KycStatus | undefined> => {
      try {
        const { cardId, cardholderAuth } = latestRef.current;
        const data = {
          card_id: cardId,
        };

        const response = await axios.post(`/v1/kycconfirm`, data, {
          headers: { Authorization: `Bearer ${cardholderAuth}`, 'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || '' },
        });
        return response.data.kyc_status as KycStatus;
      } catch {
        setServerErrors('Your submission was received but we had an issue getting the result. Please contact support');
      }
    }

    // the session is complete, either because we finished here or green id said there is nothing further to submit ie it was already 'pending'
    const onSessionComplete = async () => {
      if (sessionCompletedRef.current) {
        return;
      }
      sessionCompletedRef.current = true;
      dispatch('START_LOADING');
      const status = await handleKycConfirm();
      dispatch('STOP_LOADING');
      if(status === KycStatus.COMPLIANT) {
        push({
          pathname: '/success',
          state: {
            heading: '## KYC Verified',
            message: `#### Thank you for completing KYC verification. GreenID has verified your identity and you can now use your card.`,
          },
        });
      } else {
        push({
          pathname: '/success',
          state: {
            heading: '## Contact Support',
            message: `#### Your submission is complete but greenID could not verify your identity. Please contact support for further assistance on (03) 9000 0012.`,
          },
        });
      }
    }
    // load green id's css
    const loadStylesheet = (href: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`link[href="${href}"]`);
        if (existing) {
          resolve();
          return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.media = 'screen';
        link.href = href;

        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Failed to load ${href}`));

        document.head.appendChild(link);
      });
    // load in green ids scripts for the environment
    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));

        document.body.appendChild(script);
      });

    const loadGreenId = async () => {
      try {
        await loadStylesheet(`https://${process.env.REACT_APP_GREEN_ID_CSS_BASE_URL}/df/assets/stylesheets/greenid.css`)
        await loadScript(`https://${process.env.REACT_APP_GREEN_ID_BASE_URL}/df/javascripts/greenidConfig.js`)
        await loadScript(`https://${process.env.REACT_APP_GREEN_ID_BASE_URL}/df/javascripts/greenidui.min.js`)
        await loadScript(`https://${process.env.REACT_APP_GREEN_ID_BASE_URL}/df/javascripts/libs/jquery-3.5.1.min.js`)


        if (!window.greenidUI) {
          console.error('greenIDUI not available after loading scripts');
          return;
        }
        // set up greenidUI
        window.greenidUI.setup({
          environment: `${process.env.REACT_APP_GREEN_ID_ENV}`,
          frameId: 'GreenIdUi',
          errorCallback: onError,
          sessionCompleteCallback: onSessionComplete,
          sessionCancelledCallback: onSessionCancel,
          preSubmitValidationCallback: onSubmitValidation,
        });
        // render it
        window.greenidUI.show(
          'vaultpaymentsolutions',
          `${process.env.REACT_APP_GREEN_ID_PASSWORD}`,
          kyc.verificationToken ?? ''
        );
      } catch (err) {
        console.error('Failed to load GreenID scripts', err);
      }
    };

    loadGreenId();
  }, [dispatch, history, push, kyc.verificationToken]);

  return (
    <>
      <SectionContainer>
        {loading && (
          <GreenIdContainer>
            <Markdown
              children={
                "Checking your identity with GreenID. This may take a few seconds. Please don't refresh or leave this page"
              }
              align="center"
            />
            <LoadingSpinner show />
          </GreenIdContainer>
        )}
        {/* keep green id's frame mounted while loading so it isn't wiped and re-rendered empty */}
        <GreenIdUi id={'GreenIdUi'} hidden={loading} />
        {serverErrors && (
          <Markdown children={serverErrors} className="validationText" />
        )}
      </SectionContainer>
    </>
  );
};